import React, { useState, useEffect, useRef, useCallback } from 'react';
import './AdminLogin.css';
import { saveToken, saveUserInfo, saveThemes } from '../utils/auth';
import { applyTheme, resolveTheme } from '../theme';
import { API_URL, GOOGLE_CLIENT_ID } from '../config';

const AdminLogin = ({ onLoginSuccess }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const googleButtonRef = useRef(null);

  const finishLogin = (data) => {
    saveToken(data.token);
    saveUserInfo({
      username:     data.username,
      display_name: data.display_name || '',
      role:         data.role,
      branch_id:    data.branch_id,
      permissions:  data.permissions,
    });
    saveThemes(data.branch_theme, data.user_theme);
    applyTheme(resolveTheme(data.branch_theme, data.user_theme, null));
    if (onLoginSuccess) {
      onLoginSuccess();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/admin/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        finishLogin(data);
      } else {
        setError(data.error || 'Login failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Login error:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleCredential = useCallback(async (response) => {
    setError('');
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/google-login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ credential: response.credential }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        finishLogin(data);
      } else {
        setError(data.error || 'Google sign-in failed');
      }
    } catch (err) {
      setError('Network error. Please try again.');
      console.error('Google login error:', err);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the Google Identity Services script once, then render its button into our own
  // container -- the script draws the button itself (can't be styled via our own CSS), so it's
  // rendered as a separate, self-contained widget rather than a plain <button>.
  useEffect(() => {
    const initializeGoogleButton = () => {
      if (!window.google || !googleButtonRef.current) return;
      window.google.accounts.id.initialize({
        client_id: GOOGLE_CLIENT_ID,
        callback: handleGoogleCredential,
      });
      window.google.accounts.id.renderButton(googleButtonRef.current, {
        theme: 'outline', size: 'large', width: 300, text: 'signin_with',
      });
    };

    if (window.google) {
      initializeGoogleButton();
      return;
    }
    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = initializeGoogleButton;
    document.body.appendChild(script);
  }, [handleGoogleCredential]);

  return (
    <div className="admin-login-container">
      <div className="admin-login-card">
        <div className="admin-login-logo">
          <h1>CTTA Admin Portal</h1>
        </div>

        {error && (
          <div className="admin-login-error">
            {error}
          </div>
        )}

        <div className="google-signin-row">
          <div ref={googleButtonRef} />
        </div>

        <div className="admin-login-divider">
          <span>or</span>
        </div>

        <form onSubmit={handleSubmit} className="admin-login-form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              required
              autoComplete="username"
              disabled={loading}
            />
          </div>

          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="current-password"
              disabled={loading}
            />
          </div>

          <button type="submit" className="login-button" disabled={loading}>
            {loading ? 'Logging in...' : 'Login'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AdminLogin;
